// biome-ignore assist/source/organizeImports: Legacy import order, will refactor later
import SnackbarContext, { useSnackBarContext } from '@/contexts/SnackbarContext';
import { useQuery } from '@apollo/client';
import makeStyles from '@mui/styles/makeStyles';
import { useContext, useEffect, useState } from 'react';
import { useGetFeatureFlags } from '@/hooks/useGetFeatureFlags';
import { useTutorialContext } from '../../contexts/TutorialContext';
import { usePageTutorial } from '../../hooks/usePageTutorial';
import { useLoadPortfolioBridge } from '../../hooks/useLoadPortfolioBridge';
import { PORTFOLIO_CONTEXT } from '../../pages/PortfolioAnalysis/constants';
import { analytics, defaultDimensions, dimensions } from '../../utils/analytics';
import { getUserId } from '../../utils/auth';
import { ANALYTICS_EVENTS, ANALYTICS_EVENT_ACTIONS, CLM_PATH } from '../../utils/constants';
import { getFulltextResultParameters } from '../../utils/globalSearch';
// Feature flag: enable_consolidated_live_models
import { normalizePortfolioFilters } from '../../utils/portfolioUtils';
import { FilterBarWrapper, FilterSetsProvider, useFilterSetsContext } from '../FilterBarWrapper';
import { FilterKeys, FilterPortfolios } from '../Filters';
import {
  DEVELOPMENT_STATUS_QUERY,
  RESOURCE_THEME_QUERY,
  SHORE_STATUS_QUERY,
} from '../Filters/operations';
import { ConsolidateAssetsButton } from '../Modals/ConsolidateAssets/button';
import { useConsolidatedAssetsController } from '../Modals/ConsolidateAssets/ConsolidatedAssets.controller';
import { requestMessage } from '../Modals/ConsolidateAssets/helpers';
// Feature flag: enable_consolidated_live_models
import { PortfolioModel, PortfolioModelCLM } from '../Modals/PortfolioModel';
import { LoadPortfolio } from '../Modals/UserPortfolios/components/LoadPortfolio';
import { SavePortfolio } from '../Modals/UserPortfolios/components/SavePortfolio';
import { SharePortfolio } from '../Modals/UserPortfolios/components/SharePortfolio';
import { PageActions } from '../PageActions';
import { BRIDGE_ID_URL_SEARCH_PARAM } from '@/pages/OilAndGasMap/PortfolioBridge/PortfolioBridgePanel';
import { useCurrentUser } from '@/contexts/CurrentUserContext';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.up('md')]: {
      ...theme.sizes.fullPage,
    },
  },
  filterItem: {
    width: '25%',
    [theme.breakpoints.down('md')]: {
      width: '50%',
    },
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  filterBar: {
    gap: '0.5rem',
  },
}));

export const defaultPortfolio = {
  filters: {
    [FilterKeys.assets]: [],
    [FilterKeys.countries]: [],
    [FilterKeys.companies]: [],
    [FilterKeys.basins]: [],
    [FilterKeys.assetUnits]: [],
    [FilterKeys.resourceThemes]: [],
    [FilterKeys.shoreStatuses]: [],
    [FilterKeys.developmentStatuses]: [],
    [FilterKeys.npvMin]: '',
    [FilterKeys.npvMax]: '',
    [FilterKeys.reservesMin]: '',
    [FilterKeys.reservesMax]: '',
    [FilterKeys.productionMin]: '',
    [FilterKeys.productionMax]: '',
    [FilterKeys.operator]: false,
    [FilterKeys.nonOperator]: false,
    [FilterKeys.companyOperand]: 'OR',
    [FilterKeys.opportunities]: false,
    [FilterKeys.includeHistoricalParticipants]: false,
  },
  name: 'Portfolio',
};

const getPortfolioFromSearchParameters = searchParams => {
  const portfolio = JSON.parse(JSON.stringify(defaultPortfolio));
  switch (searchParams.entity) {
    case 'ASSET':
      portfolio.filters[FilterKeys.assets] = [
        {
          __typename: 'Asset',
          legacyId: searchParams.legacyId,
          name: searchParams.name,
          menuName: searchParams.name,
          displayName: searchParams.name,
          country: {
            __typename: 'Country',
            isoCode: searchParams.isoCode,
          },
        },
      ];
      break;
    case 'COUNTRY':
      portfolio.filters[FilterKeys.countries] = [
        {
          __typename: 'Country',
          displayName: searchParams.name,
          name: searchParams.name,
          isoCode: searchParams.isoCode,
          geom: searchParams.geom,
          region: {},
          isChosen: true,
        },
      ];
      break;
    case 'COMPANY':
      portfolio.filters[FilterKeys.companies] = [
        {
          __typename: 'Company',
          name: searchParams.name,
        },
      ];
      break;
  }
  return portfolio;
};

const getDefaultPortfolio = context => {
  const searchParams = getFulltextResultParameters();

  if (searchParams?.action) {
    const portfolio = getPortfolioFromSearchParameters(searchParams);
    return [{ ...portfolio }];
  }

  const urlParams = new URLSearchParams(window.location.search);
  const portfolioBridgeUuid = urlParams.get(BRIDGE_ID_URL_SEARCH_PARAM);

  if (portfolioBridgeUuid) {
    return [{ ...defaultPortfolio, _portfolioBridgeUuid: portfolioBridgeUuid }];
  }

  const portfolios = JSON.parse(sessionStorage.getItem(context));

  try {
    if (
      portfolios?.every(portfolio =>
        Object.keys(defaultPortfolio.filters).every(filter =>
          Object.keys(portfolio.filters).includes(filter),
        ),
      )
    ) {
      return portfolios;
    } else {
      return [{ ...defaultPortfolio }];
    }
  } catch {
    return [{ ...defaultPortfolio }];
  }
};

const PortfolioFilters = ({
  portfolios,
  applyBasicFilters,
  basicFilterTypes,
  context,
  consolidateAssetsProps,
  googleAnalyticsTag,
  addPortfolio,
  setPortfolios,
  defaultFilterTypes,
  clearStagingPortfolio,
  stagingPortfolios,
  deletePortfolio,
  filtersModalOpen,
  openPortfoliosModal,
  open,
  setOpen,
  clearPortfolios,
  loadPortfolio,
}) => {
  const { setSnack } = useContext(SnackbarContext);
  const [discountRate, setDiscountRate] = useState(10);
  const [cashFlowsModalOpen, setCashFlowsModalOpen] = useState(false);
  const [portfolioModelOpen, setPortfolioModelOpen] = useState(false);

  const classes = useStyles();
  const { currentFilterSetIndex } = useFilterSetsContext();
  const { enableConsolidatedLiveModels, consolidatedLiveModelsAdmin } = useGetFeatureFlags();
  const { isUserEntitled } = useCurrentUser();
  const consolidatedAssetsData = useConsolidatedAssetsController({
    portfolios,
  });

  // When flag is OFF: show legacy "Cash Flow" button
  // When flag is ON: show "Generate Report" and/or "Generate Model" based on entitlements
  const shouldShowCashFlow = !enableConsolidatedLiveModels && context === PORTFOLIO_CONTEXT;
  const shouldShowGenerateReport =
    enableConsolidatedLiveModels &&
    context === PORTFOLIO_CONTEXT &&
    (consolidatedLiveModelsAdmin || !isUserEntitled(CLM_PATH));
  const shouldShowGenerateModel = enableConsolidatedLiveModels && context === PORTFOLIO_CONTEXT;
  const isGenerateModelDisabled =
    enableConsolidatedLiveModels &&
    !consolidatedLiveModelsAdmin &&
    (consolidatedAssetsData.disabled || !isUserEntitled(CLM_PATH));

  const handleRequestReport = params => {
    const { setRequestingReport, requestReport, assetIds } = consolidatedAssetsData;
    const { companyIds, companyNames, assetUnits } = consolidateAssetsProps;

    setRequestingReport(true);

    const variables = {
      ...params,
      assetIds,
      companyIds,
      companyNames,
      assetUnits,
    };

    requestReport({ variables });

    setSnack({
      open: true,
      severity: 'info',
      message: requestMessage(variables.emailReport),
      disableHide: true,
    });
  };

  const { data: developmentStatusData } = useQuery(DEVELOPMENT_STATUS_QUERY);
  const { data: resourceThemeData } = useQuery(RESOURCE_THEME_QUERY);
  const { data: shoreStatusData } = useQuery(SHORE_STATUS_QUERY);

  return (
    <FilterBarWrapper
      filters={portfolios[currentFilterSetIndex].filters}
      allFilters={portfolios}
      applyFilters={filters => applyBasicFilters(filters, currentFilterSetIndex)}
      basicFilterTypes={basicFilterTypes}
      context={context}
      googleAnalyticsTag={googleAnalyticsTag}
      checkListItems={{
        developmentStatuses: developmentStatusData ? developmentStatusData.developmentStatus : [],
        resourceThemes: resourceThemeData ? resourceThemeData.resourceType : [],
        shoreStatuses: shoreStatusData ? shoreStatusData.shoreStatus : [],
      }}
      expandToSeeFiltersSelected
    >
      <FilterPortfolios
        addPortfolio={addPortfolio}
        apply={setPortfolios}
        basicFilterTypes={defaultFilterTypes}
        clearPortfolios={clearStagingPortfolio}
        context={context}
        defaultPortfolios={stagingPortfolios}
        deletePortfolio={deletePortfolio}
        googleAnalyticsTag={googleAnalyticsTag}
        isModalOpen={filtersModalOpen}
        openModalCallback={openPortfoliosModal}
        checkListItems={{
          developmentStatuses: developmentStatusData ? developmentStatusData.developmentStatus : [],
          resourceThemes: resourceThemeData ? resourceThemeData.resourceType : [],
          shoreStatuses: shoreStatusData ? shoreStatusData.shoreStatus : [],
        }}
        open={open}
        setOpen={setOpen}
      />

      <PageActions
        onOpenFilters={() => {
          setOpen(true);
          openPortfoliosModal();
        }}
        onClearFilters={clearPortfolios}
        presets={{
          filters: portfolios,
          setFilters: loadPortfolio,
          label: 'Portfolio',
          SaveComponent: props => <SavePortfolio {...props} />,
          LoadComponent: props => <LoadPortfolio {...props} />,
          ShareComponent: props => <SharePortfolio {...props} />,
          includeShare: true,
          context,
        }}
        openFiltersLabel='Build Portfolio'
        mainAction={
          shouldShowCashFlow
            ? {
                tooltip: consolidatedAssetsData.tooltipMessage,
                label: 'Cash Flow',
                onClick: () => consolidatedAssetsData.disabled || setCashFlowsModalOpen(true),
                disabled: consolidatedAssetsData.disabled,
              }
            : shouldShowGenerateReport
              ? {
                  tooltip: consolidatedAssetsData.tooltipMessage,
                  label: 'Generate Report',
                  onClick: () => consolidatedAssetsData.disabled || setCashFlowsModalOpen(true),
                  disabled: consolidatedAssetsData.disabled,
                }
              : shouldShowGenerateModel
                ? {
                    tooltip: consolidatedAssetsData.tooltipMessage,
                    label: 'Generate Model',
                    onClick: () => setPortfolioModelOpen(true),
                    disabled: isGenerateModelDisabled,
                  }
                : undefined
        }
        secondaryAction={
          shouldShowGenerateReport && shouldShowGenerateModel
            ? {
                tooltip: consolidatedAssetsData.tooltipMessage,
                label: 'Generate Model',
                onClick: () => setPortfolioModelOpen(true),
                disabled: isGenerateModelDisabled,
              }
            : undefined
        }
        className={classes.filterBar}
      />
      {context === PORTFOLIO_CONTEXT && (
        <>
          <ConsolidateAssetsButton
            consolidatedAssetsData={consolidatedAssetsData}
            consolidateAssetsProps={consolidateAssetsProps}
            open={cashFlowsModalOpen}
            setOpen={setCashFlowsModalOpen}
            discountRate={discountRate}
            setDiscountRate={setDiscountRate}
            onRequestReport={handleRequestReport}
          />
          {enableConsolidatedLiveModels ? (
            <PortfolioModelCLM
              open={portfolioModelOpen}
              setOpen={setPortfolioModelOpen}
              portfolios={portfolios}
              consolidateAssetsProps={consolidateAssetsProps}
              onRequestReport={handleRequestReport}
            />
          ) : (
            <PortfolioModel
              open={portfolioModelOpen}
              setOpen={setPortfolioModelOpen}
              portfolios={portfolios}
              consolidateAssetsProps={consolidateAssetsProps}
              onRequestReport={handleRequestReport}
            />
          )}
        </>
      )}
    </FilterBarWrapper>
  );
};

const Portfolio = ({
  context,
  googleAnalyticsTag,
  GraphingComponent,
  basicFilterTypes,
  defaultFilterTypes,
  tutorialSteps = [],
}) => {
  const classes = useStyles();
  const { setSnack } = useSnackBarContext();
  const { tutorial } = useTutorialContext();
  const [filtersModalOpen, setFiltersModalOpen] = useState(false);
  const [portfolios, setPortfolios] = useState([...getDefaultPortfolio(context)]);
  const [stagingPortfolios, setStagingPortfolios] = useState([...portfolios]);
  const [open, setOpen] = useState(false);
  const { loadPortfolioBridge } = useLoadPortfolioBridge();

  usePageTutorial({
    steps: tutorialSteps,
    feature: googleAnalyticsTag,
  });

  useEffect(() => {
    const loadFromPortfolioBridge = async () => {
      const portfolioBridgeUuid = portfolios[0]?._portfolioBridgeUuid;

      if (!portfolioBridgeUuid) return;

      const bridgeData = await loadPortfolioBridge(portfolioBridgeUuid);

      if (bridgeData?.filters) {
        const loadedPortfolio = {
          ...defaultPortfolio,
          filters: {
            ...defaultPortfolio.filters,
            assets: bridgeData?.filters?.assets || [],
          },
          name: 'Portfolio',
        };

        setPortfolios([loadedPortfolio]);
        setStagingPortfolios([loadedPortfolio]);
      }
    };

    loadFromPortfolioBridge();
  }, [loadPortfolioBridge, portfolios[0]?._portfolioBridgeUuid]);

  useEffect(() => {
    setFiltersModalOpen(filtersModalOpen => {
      if (filtersModalOpen && tutorial) {
        return false;
      }
      return filtersModalOpen;
    });
  }, [tutorial]);

  const consolidateAssetsProps = {
    companyIds: portfolios.map(portfolio => portfolio.filters.companies.map(c => c.id)),
    companyNames: portfolios.map(portfolio => portfolio.filters.companies.map(c => c.name)),
    assetUnits: portfolios.map(portfolio => portfolio.filters.assetUnits),
  };

  const addPortfolio = stagedPortfolios => {
    gtag('event', ANALYTICS_EVENTS.event, {
      event_category: googleAnalyticsTag,
      event_action: ANALYTICS_EVENT_ACTIONS.buttonClick,
      event_label: 'add_portfolio',
      userId: getUserId(),
      ...defaultDimensions,
    });
    const newPortfolio = { ...defaultPortfolio };
    setStagingPortfolios([...stagedPortfolios, newPortfolio]);
  };

  const deletePortfolio = (stagedPortfolios, idx) => {
    if (stagedPortfolios.length === 1) {
      setSnack({
        open: true,
        severity: 'error',
        message: 'Must have at least 1 portfolio!',
      });
      return;
    }
    gtag('event', ANALYTICS_EVENTS.event, {
      event_category: googleAnalyticsTag,
      event_action: ANALYTICS_EVENT_ACTIONS.buttonClick,
      event_label: 'delete_portfolio',
      userId: getUserId(),
      ...defaultDimensions,
    });

    const newPortfolios = [
      ...stagedPortfolios.slice(0, idx),
      ...stagedPortfolios.slice(idx + 1, stagedPortfolios.length),
    ];
    setStagingPortfolios(newPortfolios);
  };

  const clearStagingPortfolio = () => {
    setStagingPortfolios([{ ...defaultPortfolio }]);
  };

  const clearPortfolios = () => {
    gtag('event', ANALYTICS_EVENTS.event, {
      event_category: googleAnalyticsTag,
      event_action: ANALYTICS_EVENT_ACTIONS.buttonClick,
      event_label: 'clear_portfolio',
      userId: getUserId(),
      ...defaultDimensions,
    });
    setPortfolios([{ ...defaultPortfolio }]);
  };

  const openPortfoliosModal = () => {
    gtag('event', ANALYTICS_EVENTS.event, {
      event_category: googleAnalyticsTag,
      event_action: ANALYTICS_EVENT_ACTIONS.buttonClick,
      event_label: 'portfolios_modal',
      userId: getUserId(),
      ...defaultDimensions,
    });
    setStagingPortfolios([...portfolios]);
  };

  const applyBasicFilters = (filters, idx = 0) => {
    setPortfolios(prevPortfolios =>
      prevPortfolios.map((portfolio, index) =>
        index === idx ? { ...portfolio, filters } : portfolio,
      ),
    );
  };

  // temporary if we add back multiple portfolios
  const loadPortfolio = portfolios => {
    setPortfolios(portfolios);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: Legacy useEffect with complex dependencies, will refactor later
  useEffect(() => {
    portfolios.forEach(p => {
      const { filters } = p;
      gtag('event', ANALYTICS_EVENTS.event, {
        event_category: googleAnalyticsTag,
        event_action: ANALYTICS_EVENT_ACTIONS.filter,
        event_label: analytics(filters),
        userId: getUserId(),
        ...dimensions(filters, context),
      });
    });
    sessionStorage.setItem(context, JSON.stringify(portfolios));
  }, [portfolios]);

  return (
    <div className={classes.root}>
      <FilterSetsProvider filterSetsCount={portfolios.length}>
        <PortfolioFilters
          portfolios={portfolios}
          applyBasicFilters={applyBasicFilters}
          basicFilterTypes={basicFilterTypes}
          context={context}
          googleAnalyticsTag={googleAnalyticsTag}
          addPortfolio={addPortfolio}
          setPortfolios={setPortfolios}
          defaultFilterTypes={defaultFilterTypes}
          clearStagingPortfolio={clearStagingPortfolio}
          stagingPortfolios={stagingPortfolios}
          deletePortfolio={deletePortfolio}
          filtersModalOpen={filtersModalOpen}
          openPortfoliosModal={openPortfoliosModal}
          open={open}
          setOpen={setOpen}
          clearPortfolios={clearPortfolios}
          loadPortfolio={loadPortfolio}
          consolidateAssetsProps={consolidateAssetsProps}
        />
      </FilterSetsProvider>
      {GraphingComponent && (
        <GraphingComponent
          portfolios={normalizePortfolioFilters(portfolios)}
          consolidateAssetsProps={consolidateAssetsProps}
        />
      )}
    </div>
  );
};

export default Portfolio;
